<template>
    <!-- Calendario de la caja de reserva (diseño nuevo de la ficha).
         Mismas reglas que vue-hotel-datepicker, que es lo que se usaba antes:
           · no se puede llegar un día deshabilitado,
           · la salida puede caer como mucho EN el primer día deshabilitado
             después de la llegada (inclusive),
           · entre llegada y salida tiene que haber al menos `minNights` noches.
         Solo pinta y emite: quien lo usa guarda las fechas en el store. -->
    <div class="fx-cal" @keydown="tecla">
        <div class="fx-cal-head">
            <button type="button" class="fx-cal-nav" :disabled="esPrimerMes" :aria-label="$t('ficha_nueva.cal.prev')" @click="mover(-1)">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 5-7 7 7 7" /></svg>
            </button>
            <p class="fx-cal-info" aria-live="polite">
                <template v-if="entrada && !salida">{{ $t('ficha_nueva.cal.pick_out') }}</template>
                <template v-else-if="entrada && salida"><strong>{{ $tc('ficha_nueva.nights', noches, { n: noches }) }}</strong></template>
                <template v-else>{{ $t('ficha_nueva.cal.pick_in') }}</template>
            </p>
            <button type="button" class="fx-cal-nav" :aria-label="$t('ficha_nueva.cal.next')" @click="mover(1)">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg>
            </button>
        </div>

        <p class="fx-cal-month">{{ tituloMes }}</p>
        <div class="fx-cal-grid" role="grid" :aria-label="tituloMes">
            <span v-for="w in semana" :key="'w' + w" class="fx-cal-w" aria-hidden="true">{{ w }}</span>
            <span v-for="b in blancos" :key="'b' + b" aria-hidden="true"></span>
            <button
                v-for="d in dias"
                :key="d.iso"
                type="button"
                :class="['fx-cal-d', {
                    'is-today': d.iso === hoy,
                    'is-start': d.iso === entrada,
                    'is-end': d.iso === salida,
                    'in-range': entrada && salida && d.iso > entrada && d.iso < salida,
                    'is-blocked': d.bloqueado,
                    'is-min': d.esMin,
                }]"
                :disabled="d.off"
                :tabindex="d.iso === foco ? 0 : -1"
                :data-dia="d.iso"
                :aria-pressed="String(d.iso === entrada || d.iso === salida)"
                :aria-label="d.etiqueta"
                @click="elegir(d.iso)"
            ><span>{{ d.n }}</span></button>
        </div>

        <div class="fx-cal-foot">
            <span class="fx-cal-legend">
                <span><s>12</s> {{ $t('ficha_nueva.cal.unavailable') }}</span>
            </span>
            <button v-if="entrada" type="button" class="fx-link" @click="borrar">{{ $t('ficha_nueva.cal.clear') }}</button>
        </div>
    </div>
</template>

<script>
const iso = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const aFecha = s => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d, 12); };
const sumar = (s, n) => { const d = aFecha(s); d.setDate(d.getDate() + n); return iso(d); };
const diff = (a, b) => Math.round((aFecha(b) - aFecha(a)) / 864e5);

export default {
    name: 'FxCalendario',
    props: {
        // Fechas deshabilitadas tal como las arma calculateOccupiedDays (ISO con hora).
        disabledDates: { type: Array, default: () => [] },
        minNights: { type: Number, default: 1 },
        // 'YYYY-MM-DD' o null
        checkIn: { type: String, default: null },
        checkOut: { type: String, default: null },
    },
    data() {
        const hoy = iso(new Date());
        return { hoy, entrada: this.checkIn, salida: this.checkOut, vista: null, foco: null };
    },
    computed: {
        bloqueados() {
            const s = new Set();
            (this.disabledDates || []).forEach(d => { if (d) s.add(String(d).slice(0, 10)); });
            return s;
        },
        minimo() { return Math.max(1, Number(this.minNights) || 1); },
        // Primer día deshabilitado después de la llegada: la salida no puede pasarlo.
        tope() {
            if (!this.entrada || this.salida) return null;
            const orden = [...this.bloqueados].filter(d => d > this.entrada).sort();
            return orden[0] || null;
        },
        noches() { return this.entrada && this.salida ? diff(this.entrada, this.salida) : 0; },
        locale() { return (this.$i18n && this.$i18n.locale) || 'es'; },
        semana() {
            // lunes a domingo, en el idioma actual
            const base = new Date(2024, 0, 1, 12); // un lunes
            return Array.from({ length: 7 }, (_, i) => {
                const d = new Date(base); d.setDate(base.getDate() + i);
                return d.toLocaleDateString(this.locale, { weekday: 'short' }).replace('.', '').slice(0, 2);
            });
        },
        tituloMes() {
            const t = this.vista.toLocaleDateString(this.locale, { month: 'long', year: 'numeric' });
            return t.charAt(0).toUpperCase() + t.slice(1);
        },
        blancos() { return (this.vista.getDay() + 6) % 7; },
        esPrimerMes() {
            const h = aFecha(this.hoy);
            return this.vista.getFullYear() === h.getFullYear() && this.vista.getMonth() === h.getMonth();
        },
        dias() {
            const y = this.vista.getFullYear(), m = this.vista.getMonth();
            const total = new Date(y, m + 1, 0).getDate();
            const out = [];
            for (let n = 1; n <= total; n++) {
                const s = iso(new Date(y, m, n, 12));
                const st = this.estado(s);
                const largo = aFecha(s).toLocaleDateString(this.locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
                const extra = [
                    s === this.entrada ? this.$t('ficha_nueva.cal.arrival') : '',
                    s === this.salida ? this.$t('ficha_nueva.cal.departure') : '',
                    st.bloqueado ? this.$t('ficha_nueva.cal.unavailable') : '',
                ].filter(Boolean).join(', ');
                out.push({ iso: s, n, ...st, etiqueta: extra ? `${largo}, ${extra}` : largo });
            }
            return out;
        },
    },
    watch: {
        checkIn(v) { this.entrada = v; },
        checkOut(v) { this.salida = v; },
    },
    created() {
        // Abre en el mes de la llegada o, si no hay, en el primero donde se puede llegar.
        let inicio = this.entrada;
        if (!inicio) {
            inicio = this.hoy;
            for (let i = 0; i < 400 && !this.puedeLlegar(inicio); i++) inicio = sumar(inicio, 1);
        }
        const b = aFecha(inicio);
        this.vista = new Date(b.getFullYear(), b.getMonth(), 1, 12);
        this.foco = inicio;
    },
    methods: {
        puedeLlegar(s) { return s >= this.hoy && !this.bloqueados.has(s); },
        estado(s) {
            const bloqueado = this.bloqueados.has(s);
            const eligiendoSalida = this.entrada && !this.salida;
            let off = s < this.hoy, esMin = false;
            if (eligiendoSalida && s > this.entrada) {
                if (this.tope && s > this.tope) off = off || !this.puedeLlegar(s);  // sirve como nueva llegada
                else if (diff(this.entrada, s) < this.minimo) { off = true; esMin = true; }
                else off = false;
            } else if (bloqueado) off = true;
            return { off, bloqueado: bloqueado && !(eligiendoSalida && s === this.tope), esMin };
        },
        elegir(s) {
            this.foco = s;
            const nuevaLlegada = !this.entrada || this.salida || s <= this.entrada || (this.tope && s > this.tope);
            if (nuevaLlegada) { this.entrada = s; this.salida = null; } else this.salida = s;
            this.$emit('change', { checkIn: this.entrada, checkOut: this.salida });
            this.$nextTick(() => this.enfocar());
        },
        borrar() {
            this.entrada = null; this.salida = null;
            this.$emit('change', { checkIn: null, checkOut: null });
        },
        mover(n) {
            const v = new Date(this.vista); v.setMonth(v.getMonth() + n);
            this.vista = v;
            this.foco = iso(v) < this.hoy ? this.hoy : iso(v);
        },
        enfocar() {
            const b = this.$el.querySelector(`[data-dia="${this.foco}"]`);
            if (b && !b.disabled) b.focus();
        },
        tecla(e) {
            if (!e.target.closest || !e.target.closest('.fx-cal-d')) return;
            const paso = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[e.key];
            if (!paso) return;
            e.preventDefault();
            const nuevo = sumar(this.foco, paso);
            if (nuevo < this.hoy) return;
            this.foco = nuevo;
            const f = aFecha(nuevo);
            if (f.getMonth() !== this.vista.getMonth() || f.getFullYear() !== this.vista.getFullYear()) this.vista = new Date(f.getFullYear(), f.getMonth(), 1, 12);
            this.$nextTick(() => { const b = this.$el.querySelector(`[data-dia="${nuevo}"]`); if (b) b.focus(); });
        },
    },
};
</script>
